import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { Party } from "../../lib/types";
import { Avatar } from "./Avatar";

interface PartyCardProps {
  party: Party;
  left?: React.ReactNode;
  showHost?: boolean;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  to?: string;
}

export function PartyCard({
  party,
  left,
  showHost = false,
  subtitle,
  badge,
  to,
}: PartyCardProps) {
  const { t } = useTranslation();
  const href = to ?? `/party/${party.slug}`;
  const avatars = [
    ...(showHost && party.user
      ? [{ name: party.user.name, imageUrl: party.user.avatar_url }]
      : []),
    ...party.guests.map((g) => ({
      name: g.celebrity.name,
      imageUrl: g.celebrity.image_url,
    })),
  ];

  return (
    <Link
      to={href}
      className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-4 hover:bg-stone-100 dark:hover:bg-stone-800 border border-stone-200 dark:border-stone-800 border-l-2 border-l-transparent hover:border-l-amber-500 transition-colors"
    >
      {left}

      {/* Avatar stack */}
      <div className="flex -space-x-2 flex-shrink-0">
        {avatars.length > 0 ? (
          avatars.map((a, i) => (
            <div
              key={i}
              className="ring-2 ring-white dark:ring-stone-900 rounded-full"
            >
              <Avatar name={a.name} imageUrl={a.imageUrl} size={32} />
            </div>
          ))
        ) : (
          <div className="w-9 h-9 rounded-full bg-stone-100 dark:bg-stone-800 border-2 border-dashed border-stone-300 dark:border-stone-700" />
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-stone-900 dark:text-stone-100 truncate">
            {party.title}
          </p>
          {badge}
        </div>
        <p className="text-stone-500 text-xs mt-0.5">
          {subtitle ?? party.user?.name ?? "Unknown"}
        </p>
      </div>

      {/* Vote count */}
      <div className="text-right flex-shrink-0 min-w-[3rem]">
        <p className="text-amber-500 font-bold text-2xl tabular-nums leading-none">
          {party.vote_count ?? 0}
        </p>
        <p className="text-stone-400 dark:text-stone-600 text-xs mt-0.5">
          {t("card.votes")}
        </p>
      </div>
    </Link>
  );
}
